import { useState } from "react";
import { FiChevronRight, FiSend } from "react-icons/fi";
import { CREATE_COMMENT } from "../../services/graphql/queriesMutations";
import { useMutation } from "@apollo/client";
import { useGlobalContext } from "../context/AuthContext";

interface Props {
  editId?: number;
  gistId?: number;
  userId?: number;
  parentId?: number;
  setShowModal: (value: boolean) => void;
  handleRefetchComments: () => void;
}

const COMMENT_LIMIT = 3000;

const SendComment = ({
  editId,
  gistId,
  parentId,
  setShowModal,
  handleRefetchComments,
}: Props) => {
  const [createComment] = useMutation(CREATE_COMMENT);
  const [content, setContent] = useState("");
  const { user } = useGlobalContext();

  const handleClick = async () => {
    if (!user) {
      alert("Please login to comment");
      return;
    }

    if (content.trim() === "") return;

    try {
      await createComment({
        variables: {
          newComment: {
            comment: content,
            parentId: parentId || null,
            gistId,
            editId,
          },
        },
      });
      handleRefetchComments();
      setContent("");
    } catch (error: any) {
      console.error("❌ Failed to create comment:", error);
      alert(error.message || "Failed to create comment");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!user) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className="gist-comment-composer" aria-label="Comment and reply actions">
      <div className="gist-quick-comment-panel">
        <div className="gist-comment-panel-title">Quick comment on this gist</div>

        <div className="gist-comment-input-area">
          <textarea
            value={content}
            disabled={!user}
            onKeyDown={handleKeyDown}
            onChange={(e) => setContent(e.target.value)}
            className="gist-comment-textarea"
            rows={3}
            maxLength={COMMENT_LIMIT}
            placeholder={user ? "Share a quick thought…" : "Log in to comment"}
            aria-label="Quick comment on this gist"
          />
          <div className="gist-comment-input-footer" aria-hidden="true">
            <span className="gist-comment-counter">
              {content.length}/{COMMENT_LIMIT}
            </span>
          </div>
        </div>

        <button
          disabled={!user || content.trim() === ""}
          onClick={handleClick}
          className="gist-comment-send-button"
          type="button"
          title="Send quick comment"
          aria-label="Send quick comment"
        >
          <FiSend />
        </button>
      </div>

      <div className="gist-comment-divider" aria-hidden="true" />

      <div className="gist-comment-reply-zone">
        <button
          disabled={!user}
          onClick={() => setShowModal(true)}
          className="gist-reply-gist-button"
          type="button"
          title="Create a full reply gist"
          aria-label="Create a full reply gist"
        >
          <span className="gist-reply-logo-mark" aria-hidden="true">G</span>
          <span className="gist-reply-copy">
            <span className="gist-reply-title">Reply</span>
            <span className="gist-reply-subtitle">Create a full reply gist</span>
          </span>
          <FiChevronRight className="gist-reply-arrow" aria-hidden="true" />
        </button>

        <p className="gist-reply-note">
          <span className="gist-reply-note-spark" aria-hidden="true">✦</span>
          Replies create a new gist visible to the community.
        </p>
      </div>
    </div>
  );
};

export default SendComment;
